import { useMemo, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { InfiniteData, useQueryClient } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import {
  useTriggerWorkflowStateEvent,
  useWorkflowStateEventStatuses,
} from '@/service-library/hooks/events'
import { useGetWorkflowStateEvents } from '@/service-library/hooks/workflow-state-events'
import { Document } from '@/types/documents'
import { DocumentFlag, RowValueFlag } from '@/types/flags'
import PaginatedResponse from '@/types/paginated-response'
import {
  EventStatusDetails,
  WorkflowStateEvent,
} from '@/types/workflow-state-events'
import { getFlagsByLevel, getFlagsCountFromLevel } from '@/utils/flags'
import generateUuid from '@/utils/uuid'
import { SYSTEM_EVENT_CODES_FILTER } from '@/utils/workflow-state-events'
import useFlagPriorityColor from '@/components/flags/useFlagPriorityColor'
import { useNotifications } from '@/components/notifications/NotificationProvider'
import DocumentsWithErrorsDialog from './DocumentsWithErrorsDialog'
import EventButton from './EventButton'
import { useDocumentRowValuesContext } from './providers/DocumentRowValuesProvider'

const RUNNING_STATUSES = ['waiting', 'running', 'running_async']

type ValidationEventButtonProps = {
  document: Document
  goToNextAvailableDocumentOrClose: () => void
  workflowStateId: string
}

export default function ValidationEventButton({
  document,
  goToNextAvailableDocumentOrClose,
  workflowStateId,
}: ValidationEventButtonProps) {
  const [triggeredEventDetails, setTriggeredEventDetails] = useState<{
    event: WorkflowStateEvent
    stayOnCurrenDoc: boolean
  }>()

  const hasErrorsOverlay = useOverlay()
  const isInFlight = useMemo(
    () =>
      document.document_workflow_states?.some(
        ({ processing_started_at }) => processing_started_at,
      ) || false,
    [document.document_workflow_states],
  )

  const { rowValueFlags } = useDocumentRowValuesContext()

  const { workflowStateEvents } = useGetWorkflowStateEvents({
    filters: {
      workflow_state_id: workflowStateId,
      limit: '100',
      'code__in!': SYSTEM_EVENT_CODES_FILTER,
    },
  })

  const visibleWorkflowStateEvents = workflowStateEvents.filter(
    ({ allow_manual_trigger }) => allow_manual_trigger,
  )

  const {
    eventStatuses,
    refetch: refetchEventStatuses,
    isFetching,
    queryKey,
  } = useWorkflowStateEventStatuses({
    filters: {
      document_id: document.id,
    },
    enabled: !!visibleWorkflowStateEvents.length,
    refetchInterval: (query) => {
      const allResults =
        query.state.data?.pages?.flatMap((page) => page.results) ?? []
      const atLeastOneRunning = allResults.some(
        (eventInstance) =>
          !eventInstance.is_cache_update &&
          RUNNING_STATUSES.includes(eventInstance.status),
      )
      return atLeastOneRunning ? 2000 : false
    },
  })

  const queryClient = useQueryClient()
  const { triggerWorkflowStateEvent } = useTriggerWorkflowStateEvent({
    onMutate: async (item) => {
      await queryClient.cancelQueries({ queryKey })

      // Update the list data in cache - insert each trigger body as a separate event
      queryClient.setQueryData<
        InfiniteData<PaginatedResponse<EventStatusDetails>>
      >(queryKey, (old) => {
        // Convert each trigger body to an optimistic event status
        const newEventStatuses: EventStatusDetails[] = item.map(
          (triggerBody) => ({
            id: generateUuid(), // This is a temporary ID, but we need one
            document_id: triggerBody.document_id,
            status: triggerBody.status,
            current_action_code: '',
            started_at: new Date().toISOString(),
            workflow_state_event_id: triggerBody.workflow_state_event_id,
            is_cache_update: true,
          }),
        )

        if (!old || !old.pages || old.pages.length === 0) {
          // Create first page if none exist
          return {
            pages: [
              {
                results: newEventStatuses,
                next: null,
                previous: null,
              },
            ],
            pageParams: [undefined],
          }
        }

        const pages = old.pages.map((page, index) => {
          if (index !== old.pages.length - 1) return page

          return {
            ...page,
            results: [...page.results, ...newEventStatuses],
          }
        })

        return {
          ...old,
          pages,
        }
      })
    },
  })

  const runningEvent = useMemo(() => {
    const runningEventInstance = eventStatuses.find((eventInstance) => {
      return RUNNING_STATUSES.includes(eventInstance.status)
    })
    return workflowStateEvents.find(
      (event) => event.id === runningEventInstance?.workflow_state_event_id,
    )
  }, [eventStatuses, workflowStateEvents])

  const { document_flags = [] } = document

  const documentFlagsByLevel = getFlagsByLevel(document_flags)
  const rowFlagsByLevel = getFlagsByLevel(rowValueFlags)

  const { priorityLevel } = useFlagPriorityColor({
    documentFlags: getFlagsCountFromLevel(documentFlagsByLevel),
    rowsFlags: getFlagsCountFromLevel(rowFlagsByLevel),
  })

  const documentErrorFlags = documentFlagsByLevel.error as DocumentFlag[]
  const rowValueErrorFlags = rowFlagsByLevel.error as RowValueFlag[]
  const documentWarningFlags = documentFlagsByLevel.warning as DocumentFlag[]
  const rowValueWarningFlags = rowFlagsByLevel.warning as RowValueFlag[]
  const hasProblemFlags =
    priorityLevel === 'error' || priorityLevel === 'warning'

  function triggerEvent(event: WorkflowStateEvent, stayOnDoc: boolean) {
    triggerWorkflowStateEvent([
      {
        workflow_state_event_id: event.id,
        document_id: document.id,
        status: 'waiting',
      },
    ])

    if (stayOnDoc) return

    if (
      event.move_to_next_doc_in_validation ||
      event.move_to_next_doc_in_validation === undefined
    ) {
      goToNextAvailableDocumentOrClose()
    }
  }

  function handleTriggerEvent(
    event: WorkflowStateEvent,
    keys: { shiftKey: boolean }, // Using this structure since the manual trigger will pass a mouse event
  ) {
    if (
      hasProblemFlags &&
      event.when_document_has_errors === 'trigger_with_warning'
    ) {
      hasErrorsOverlay.open()
      setTriggeredEventDetails({
        event,
        stayOnCurrenDoc: !!keys?.shiftKey,
      })
    } else {
      triggerEvent(event, !!keys?.shiftKey)
    }
  }

  useNotifications({
    keys: [
      'pdWorkflow/events.event_complete',
      'pdWorkflow/events.document_active',
    ],
    callback: ({ updated_entity_ids }) => {
      if (updated_entity_ids.includes(document.id)) {
        refetchEventStatuses()
      }
    },
  })

  useHotkeys(
    ['alt+d', 'shift+alt+d'],
    (_e, handler) => {
      !runningEvent &&
        visibleWorkflowStateEvents[0] &&
        // hotkey triggers primary event
        handleTriggerEvent(visibleWorkflowStateEvents[0], {
          shiftKey: !!handler.shift,
        })
    },
    {
      preventDefault: true,
      enableOnFormTags: true,
    },
    [
      hasProblemFlags,
      goToNextAvailableDocumentOrClose,
      visibleWorkflowStateEvents[0],
    ],
  )

  return visibleWorkflowStateEvents.length > 0 ? (
    <>
      <EventButton
        documentsHaveErrors={hasProblemFlags}
        documentsIds={[document.id]}
        workflowStateEvents={visibleWorkflowStateEvents}
        isLoading={isFetching || isInFlight || !!runningEvent}
        onTriggerEvent={handleTriggerEvent}
        buttonText={runningEvent?.name}
      />
      <DocumentsWithErrorsDialog
        overlay={hasErrorsOverlay}
        documentFlags={[...documentErrorFlags, ...documentWarningFlags]}
        rowValueFlags={[...rowValueErrorFlags, ...rowValueWarningFlags]}
        onContinue={() => {
          triggeredEventDetails &&
            triggerEvent(
              triggeredEventDetails.event,
              triggeredEventDetails.stayOnCurrenDoc,
            )
          setTriggeredEventDetails(undefined)
          hasErrorsOverlay.close()
        }}
      />
    </>
  ) : null
}
